import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  IconButton,
  Box,
  Slide,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    zIndex: 9999,
    background:
      "linear-gradient(135deg, rgb(57 57 57 / 78%) 0%, rgb(10 10 20 / 93%) 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  closeButton: {
    position: "fixed",
    top: 32,
    right: 32,
    color: "#fff",
    bgcolor: "rgba(30,30,30,0.38)",
    zIndex: 10001,
    "&:hover": { bgcolor: "rgba(255,255,255,0.16)", color: "#fff" },
    boxShadow: 2,
  },
  imageContainer: {
    width: "80vw",
    height: "80vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  loaderContainer: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },
  slideContainer: {
    maxWidth: "100%",
    maxHeight: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  image: {
    maxWidth: "90%",
    maxHeight: "90vh",
    borderRadius: 12,
    background: "#fff",
    boxShadow: "0 2px 16px rgba(0,0,0,0.18)",
    objectFit: "contain",
  },
  navButton: {
    position: "fixed",
    top: "50%",
    color: "#1976d2",
    bgcolor: "#e3f2fd",
    border: "2px solid #1976d2",
    width: 48,
    height: 48,
    "&:hover": { bgcolor: "#1976d2", color: "#fff" },
    boxShadow: 3,
  },
  prevButton: {
    left: 32,
  },
  nextButton: {
    right: 32,
  },
  zoomControls: {
    position: "fixed",
    bottom: 32,
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    gap: 2,
    alignItems: "center",
    bgcolor: "rgba(0,0,0,0.5)",
    px: 2,
    py: 1,
    borderRadius: 4,
  },
  zoomButton: {
    color: "white",
  },
};

export default function ImagePreviewModal({
  images = [],
  imageData = [],
  open,
  onClose,
  initialIndex = 0,
}) {
  const [current, setCurrent] = useState(initialIndex);
  const [direction, setDirection] = useState("right");
  const [imageStatus, setImageStatus] = useState({});
  const zoomLevels = [0.75, 1, 1.5];
  const [zoomIdx, setZoomIdx] = useState(1);
  const zoom = zoomLevels[zoomIdx];

  // Track image loading status
  const updateImageStatus = (url, status) => {
    setImageStatus((prev) => ({
      ...prev,
      [url]: status, // 'loading', 'loaded', 'error'
    }));
  };

  // Preload and track image status
  useEffect(() => {
    if (!open || !images.length) return;

    images.forEach((url) => {
      if (!imageStatus[url]) {
        updateImageStatus(url, "loading");
        const img = new Image();
        img.src = url;
        img.onload = () => updateImageStatus(url, "loaded");
        img.onerror = () => {
          console.error(`Failed to load image: ${url}`);
          updateImageStatus(url, "error");
        };
      }
    });
  }, [open, images]);

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      setCurrent(initialIndex);
      setZoomIdx(1);
    }
  }, [open, initialIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, current, images]);

  const handlePrev = () => {
    setDirection("left");
    setCurrent((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setDirection("right");
    setCurrent((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleZoomIn = () =>
    setZoomIdx((i) => Math.min(i + 1, zoomLevels.length - 1));
  const handleZoomOut = () => setZoomIdx((i) => Math.max(i - 1, 0));

  if (!open || !images.length) return null;

  const currentImage = images[current];
  const status = imageStatus[currentImage] || "loading";

  const modalContent = (
    <Box
      id="image-carousel-overlay"
      onClick={(e) => e.target.id === "image-carousel-overlay" && onClose()}
      sx={styles.overlay}
    >
      {/* Close button */}
      <IconButton onClick={onClose} sx={styles.closeButton} size="large">
        <CloseIcon />
      </IconButton>

      {/* Image container */}
      <Box sx={styles.imageContainer}>
        {status === "loading" && (
          <Box sx={styles.loaderContainer}>
            <CircularProgress sx={{ color: "white" }} />
          </Box>
        )}

        {status === "error" && (
          <Typography color="white" variant="h6">
            Failed to load image
          </Typography>
        )}

        <Slide
          in={status === "loaded"}
          direction={direction}
          timeout={400}
          mountOnEnter
          unmountOnExit
        >
          <Box sx={styles.slideContainer}>
            <img
              src={currentImage}
              alt={`Preview ${current + 1}`}
              style={{
                ...styles.image,
                maxWidth: `${90 * zoom}%`,
                maxHeight: `${90 * zoom}vh`,
                transform: `scale(${zoom})`,
                transition: "transform 0.4s ease",
              }}
              onError={(e) => {
                e.target.onerror = null;
                updateImageStatus(currentImage, "error");
              }}
            />
          </Box>
        </Slide>
      </Box>

      {/* Navigation controls */}
      {images.length > 1 && (
        <>
          <IconButton
            onClick={handlePrev}
            sx={{ ...styles.navButton, ...styles.prevButton }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>

          <IconButton
            onClick={handleNext}
            sx={{ ...styles.navButton, ...styles.nextButton }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </>
      )}

      {/* Zoom controls */}
      <Box sx={styles.zoomControls}>
        <IconButton
          onClick={handleZoomOut}
          disabled={zoomIdx === 0}
          sx={styles.zoomButton}
        >
          <ZoomOutIcon />
        </IconButton>
        <Typography color="white">{Math.round(zoom * 100)}%</Typography>
        <IconButton
          onClick={handleZoomIn}
          disabled={zoomIdx === zoomLevels.length - 1}
          sx={styles.zoomButton}
        >
          <ZoomInIcon />
        </IconButton>
      </Box>
    </Box>
  );

  // Render modal at document body level using Portal
  return createPortal(modalContent, document.body);
}
